<script setup lang="ts">
import { computed, ref } from 'vue';
import BottomSheetModal from '@Components/BottomSheetModal.vue';
import BrandedCityscape from '@Components/welcome/illustrations/BrandedCityscape.vue';
import IllustrationWelcomeShield from '@Components/welcome/illustrations/IllustrationWelcomeShield.vue';
import { useCompanyStore } from '@Stores/company.store';
import { useDashboardStore } from '@Stores/dashboard.store';
import { CompanyLogoDisplay } from '@Shared';
import useCompanyImageBaseUrl from '@Use/useCompanyImageBaseUrl';
import useDealLogoUrl from '@Use/useDealLogoUrl';
import { getBasePath } from '@Src/mfe.config';

withDefaults(defineProps<{
    open: boolean;
    isSubmitting?: boolean;
    error?: string | null;
}>(), {
    isSubmitting: false,
    error: null,
});

const emit = defineEmits<{
    submit: [messagingOptIn: boolean];
}>();

const companyStore = useCompanyStore();
const dashboardStore = useDashboardStore();
const companyImageBaseUrl = useCompanyImageBaseUrl();

const companyLogoUrl = useDealLogoUrl(companyImageBaseUrl, companyStore, dashboardStore);

// Co-branded RCS legal pages live at the client hub origin under the company's
// slug (DS-4798). The host injects the slug-prefixed base path (e.g. /{slug}).
// Open in a new tab so the opt-in sheet stays put; fall back to the corporate
// pages if the base path is unavailable.
const legalTermsUrl = computed(() => {
    const base = getBasePath();
    return base ? `${base}/legal/terms` : 'https://www.certifid.com/company/terms-of-service';
});
const legalPrivacyUrl = computed(() => {
    const base = getBasePath();
    return base ? `${base}/legal/privacy` : 'https://www.certifid.com/company/privacy-policy';
});

// Carrier compliance: opt-in is explicit, not pre-checked. Matches the
// certifid-vision prototype.
const messagingOptIn = ref(false);

const handleSubmit = () => emit('submit', messagingOptIn.value);
</script>

<template>
    <BottomSheetModal
        :open="open"
        :dismissable="false"
        dialog-label="Welcome"
    >
        <div class="welcome-sheet">
            <div class="welcome-sheet__logo">
                <CompanyLogoDisplay
                    v-if="companyStore.company"
                    :company-logo-url="companyLogoUrl"
                    :company="companyStore.company"
                    :brand="companyStore.brand"
                />
                <span v-else class="welcome-sheet__logo-text">CertifID</span>
            </div>

            <div class="welcome-sheet__illustration">
                <BrandedCityscape class="welcome-sheet__cityscape" />
                <IllustrationWelcomeShield class="welcome-sheet__shield" />
            </div>

            <h1 class="welcome-sheet__headline">Move forward with confidence</h1>
            <p class="welcome-sheet__subtext">
                Everything you need to complete your closing, all in one secure place.
            </p>

            <div class="welcome-sheet__consent">
                <app-checkbox v-model="messagingOptIn">
                    I agree to receive messages about my real estate closing.
                    Msg &amp; data rates may apply.
                </app-checkbox>
            </div>

            <p v-if="error" class="welcome-sheet__error" role="alert">{{ error }}</p>

            <app-button
                type="primary"
                size="large"
                expanded
                :is-loading="isSubmitting"
                :is-disabled="isSubmitting"
                @onClick="handleSubmit"
            >
                Get Started
            </app-button>

            <div class="welcome-sheet__legal">
                <app-link :link-url="legalTermsUrl" type="external">
                    Terms of Service
                </app-link>
                <span aria-hidden="true">·</span>
                <app-link :link-url="legalPrivacyUrl" type="external">
                    Privacy Policy
                </app-link>
            </div>
        </div>
    </BottomSheetModal>
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

// White check on transparent ground — used as background-image to render the
// AppCheckbox checked state (workaround for the upstream uilib mask bug, see
// `&__consent .checkbox--checked` below).
$check-icon: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'><path fill='white' d='M7.75781 15.2266L3.19141 10.6875C3.0638 10.5599 3 10.3958 3 10.1953C3 9.99479 3.0638 9.83073 3.19141 9.70312L4.20312 8.71875C4.33073 8.57292 4.48568 8.5 4.66797 8.5C4.86849 8.5 5.04167 8.57292 5.1875 8.71875L8.25 11.7812L14.8125 5.21875C14.9583 5.07292 15.1224 5 15.3047 5C15.5052 5 15.6693 5.07292 15.7969 5.21875L16.8086 6.20312C16.9362 6.33073 17 6.49479 17 6.69531C17 6.89583 16.9362 7.0599 16.8086 7.1875L8.74219 15.2266C8.61458 15.3724 8.45052 15.4453 8.25 15.4453C8.04948 15.4453 7.88542 15.3724 7.75781 15.2266Z'/></svg>");

.welcome-sheet {
    display: flex;
    flex-direction: column;
    gap: 0;
    padding: 0;

    // Logo block — flex-centered so block-level children (CompanyLogoDisplay's
    // <app-image> wrapper, or its text fallback which uses display:-webkit-box
    // for line-clamping) center properly. text-align:center alone wouldn't
    // center either, since both render as block-level boxes. Generous
    // breathing room matches prototype pt-8 pb-6.
    &__logo {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 24px 0;
    }

    &__logo-text {
        font-size: $text-portal-body-lg;
        font-weight: 700;
        color: $portal-text;
        letter-spacing: 0.02em;
    }

    // Illustration sits on white — no card behind it. Brand color is
    // inherited via `color:` so the cityscape's currentColor fills + the
    // shield SVG both pick up the company's brand. Theme tokens flow via
    // applyTheme() → :root --ch-primary, so this surface is whitelabel-aware.
    &__illustration {
        position: relative;
        width: 100%;
        color: var(--ch-primary, #156FBE);
        margin-bottom: 32px;
    }

    &__cityscape {
        width: 100%;
        height: auto;
        display: block;
    }

    // Shield centered over the cityscape (matches prototype's
    // top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2).
    &__shield {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: auto;
        height: 64px;
    }

    &__headline {
        font-size: $text-portal-h3;
        font-weight: 700;
        color: $portal-text;
        text-align: center;
        margin: 0 0 12px;
    }

    // Subtext: narrower than headline (matches prototype's px-4 self-padding).
    &__subtext {
        font-size: $text-portal-body;
        color: $portal-text-mid;
        text-align: center;
        margin: 0 0 24px;
        padding: 0 16px;
    }

    &__consent {
        padding: 16px 8px;
        border-top: 1px solid $portal-border;
        border-bottom: 1px solid $portal-border;
        margin-bottom: 24px;
        font-size: $text-portal-caption;
        color: $portal-text-mid;
        line-height: 1.5;
    }

    &__error {
        font-size: $text-portal-caption;
        color: $red;
        margin: 0 0 12px;
        text-align: center;
    }

    &__legal {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 8px;
        font-size: $text-portal-caption;
        color: $portal-text-mid;
        margin-top: 16px;

        // Brand-tinted underlined links to match prototype's
        // "underline text-primary".
        :deep(.app-link) {
            color: var(--ch-primary, #156FBE);
            text-decoration: underline;
        }
    }
}
</style>
